/**
 * Sentry Error Tracking & Performance Monitoring
 * 
 * Setup Guide:
 * 1. Create account at https://sentry.io/
 * 2. Create new project → Select "React"
 * 3. Copy DSN and add to .env: VITE_SENTRY_DSN=https://...
 * 4. (Optional) Add auth token for source maps: SENTRY_AUTH_TOKEN=...
 */

import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  // Only initialize if DSN is provided
  if (!dsn) {
    console.info('Sentry DSN not configured - error tracking disabled');
    return;
  }

  const environment = import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_APP_VERSION || 'unknown';

  Sentry.init({
    dsn,
    environment,
    release: `swiss-os-insight@${release}`,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Performance: Sample 10% of transactions in production
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay: Sample 10% of sessions
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
    
    // Session Replay: 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,

    // Filter out sensitive data
    beforeSend(event, hint) {
      // Don't send events in development (unless you want to test)
      if (environment === 'development' && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('[Sentry] Event blocked in development:', event);
        return null;
      }

      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            // Remove API keys, tokens, passwords
            const sensitiveKeys = ['api_key', 'token', 'password', 'secret', 'authorization'];
            sensitiveKeys.forEach(key => {
              if (breadcrumb.data?.[key]) {
                breadcrumb.data[key] = '[REDACTED]';
              }
            });
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // ResizeObserver loop errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed',
    ],
  });

  console.info(`[Sentry] Initialized in ${environment} mode`);
}

// Helper to manually capture errors
export function captureError(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.setContext('custom', context);
  }
  Sentry.captureException(error);
}

// Helper to add user context
export function setUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser(user);
}

// Helper to clear user context (on logout)
export function clearUser() {
  Sentry.setUser(null);
}

// Helper to add custom tags
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Helper to add breadcrumb
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
}
